import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import {
    doc,
    getDoc,
    setDoc,
    Timestamp
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { auth, db, storage } from '../../firebase';
import PosterHeader from '../../components/header/PosterHeaderBar/PosterHeader';
import Footer from '../../components/footer/footer';
import './BlogEditor.css';

function BlogEditor() {
    const { postId } = useParams();
    const navigate = useNavigate();
    const fileInputRef = useRef(null);

    const [post, setPost] = useState({
        title: '',
        content: '',
        imageUrl: '',
        date: new Date()
    });

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isAdmin, setIsAdmin] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [previewUrl, setPreviewUrl] = useState('');
    const [error, setError] = useState('');

    const isEditing = postId !== 'new';

    // Check if user is authenticated and admin
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (!user) {
                // Not logged in, redirect to admin login
                navigate('/blog/admin');
                return;
            }

            // Check if user is admin
            const userDoc = await getDoc(doc(db, 'users', user.uid));
            if (!userDoc.exists() || !userDoc.data().isAdmin) {
                navigate('/blog');
                return;
            }

            setIsAdmin(true);

            // If editing an existing post, fetch its data
            if (isEditing) {
                await fetchPost();
            } else {
                // For new posts, create a post with today's date
                setPost({
                    title: '',
                    content: '',
                    imageUrl: '',
                    date: new Date()
                });
                setLoading(false);
            }
        });

        return () => unsubscribe();
    }, [isEditing, navigate, postId]);

    // Fetch existing post data
    const fetchPost = async () => {
        try {
            const postDoc = await getDoc(doc(db, 'blogPosts', postId));

            if (postDoc.exists()) {
                const postData = postDoc.data();

                // Convert Firestore timestamp to JS Date
                if (postData.date && postData.date.toDate) {
                    postData.date = postData.date.toDate();
                }

                setPost({
                    ...postData
                });

                if (postData.imageUrl) {
                    setPreviewUrl(postData.imageUrl);
                }
            } else {
                setError('Post not found');
            }

            setLoading(false);
        } catch (error) {
            console.error('Error fetching post:', error);
            setError('Failed to load post data');
            setLoading(false);
        }
    };

    // Handle input changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        setPost(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Handle image upload
    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Preview the image
        const reader = new FileReader();
        reader.onload = (e) => {
            setPreviewUrl(e.target.result);
        };
        reader.readAsDataURL(file);

        // Upload to Firebase Storage
        uploadImage(file);
    };

    // Handle removing the image
    const handleRemoveImage = async () => {
        if (post.imageUrl) {
            // If there's an existing image URL, extract the path
            const imagePath = post.imageUrl.split('?')[0].split('o/')[1];
            if (imagePath) {
                try {
                    // Delete the image from Firebase Storage
                    const decodedPath = decodeURIComponent(imagePath);
                    const imageRef = ref(storage, decodedPath);
                    await deleteObject(imageRef);
                } catch (error) {
                    console.error('Error deleting image:', error);
                }
            }
        }

        setPost(prev => ({
            ...prev,
            imageUrl: ''
        }));
        setPreviewUrl('');

        // Reset file input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Upload image to Firebase Storage
    const uploadImage = (file) => {
        setUploadingImage(true);
        setUploadProgress(0);

        // Create a unique filename
        const timestamp = new Date().getTime();
        const filename = `blog-images/${timestamp}_${file.name}`;
        const storageRef = ref(storage, filename);

        // Create upload task
        const uploadTask = uploadBytesResumable(storageRef, file);

        // Monitor upload progress
        uploadTask.on('state_changed',
            (snapshot) => {
                // Calculate progress
                const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                setUploadProgress(progress);
            },
            (error) => {
                console.error('Error uploading image:', error);
                setError('Failed to upload image');
                setUploadingImage(false);
            },
            () => {
                // Upload completed, get download URL
                getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
                    setPost(prev => ({
                        ...prev,
                        imageUrl: downloadURL
                    }));
                    setUploadingImage(false);
                });
            }
        );
    };

    // Save the post to Firestore
    const handleSave = async () => {
        if (!isAdmin) return;

        setSaving(true);
        setError('');

        try {
            const postToSave = {
                ...post,
                date: Timestamp.fromDate(post.date || new Date()),
                updatedAt: Timestamp.fromDate(new Date())
            };

            // Use the postId from params if editing, or create a new post with today's date as ID
            const savePostId = isEditing ? postId : `post_${new Date().toISOString().split('T')[0]}`;

            await setDoc(doc(db, 'blogPosts', savePostId), postToSave);

            // Navigate to the post view page
            navigate(`/blog/post/${savePostId}`);
        } catch (error) {
            console.error('Error saving post:', error);
            setError('Failed to save post. Please try again.');
            setSaving(false);
        }
    };

    // Cancel editing and go back
    const handleCancel = () => {
        navigate(isEditing ? `/blog/post/${postId}` : '/blog');
    };

    // If not admin, don't render the editor
    if (!isAdmin && !loading) {
        return null;
    }

    return (
        <div className="blog-editor-page">
            <PosterHeader />

            <div className="editor-container">
                <h1 className="editor-title">
                    {isEditing ? 'Edit Post' : 'Create New Post'}
                </h1>

                {error && <div className="error-message">{error}</div>}

                {loading ? (
                    <div className="loading">Loading...</div>
                ) : (
                    <div className="editor-form">
                        <div className="form-group">
                            <label htmlFor="title">Title</label>
                            <input
                                type="text"
                                id="title"
                                name="title"
                                value={post.title}
                                onChange={handleChange}
                                placeholder="Enter post title (optional)"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="content">Content</label>
                            <textarea
                                id="content"
                                name="content"
                                value={post.content}
                                onChange={handleChange}
                                placeholder="Write your blog post content here..."
                                rows={12}
                            />
                            <div className="editor-help">
                                <p>Formatting tips:</p>
                                <ul>
                                    <li>Use double line breaks for paragraphs</li>
                                    <li>Use **text** for bold text</li>
                                </ul>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Image</label>
                            <div className="image-upload-container">
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    accept="image/*"
                                    onChange={handleImageChange}
                                    className="image-input"
                                />
                                <button
                                    type="button"
                                    className="upload-image-btn"
                                    onClick={() => fileInputRef.current.click()}
                                    disabled={uploadingImage}
                                >
                                    {uploadingImage ? 'Uploading...' : 'Select Image'}
                                </button>

                                {uploadingImage && (
                                    <div className="upload-progress">
                                        <div
                                            className="progress-bar"
                                            style={{ width: `${uploadProgress}%` }}
                                        ></div>
                                        <span>{Math.round(uploadProgress)}%</span>
                                    </div>
                                )}

                                {previewUrl && (
                                    <div className="image-preview">
                                        <img src={previewUrl} alt="Post preview" />
                                        <button
                                            type="button"
                                            className="remove-image-btn"
                                            onClick={handleRemoveImage}
                                        >
                                            Remove Image
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="editor-actions">
                            <button
                                type="button"
                                className="cancel-btn"
                                onClick={handleCancel}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="save-btn"
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving ? 'Saving...' : 'Save Post'}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <Footer />
        </div>
    );
}

export default BlogEditor; 